import { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import type { PitcherReadiness } from '../../lib/pitcher-readiness';
import { analyzeFairness, anyFairnessRuleOn, FAIRNESS_VISIBLE_CAP } from '../../lib/fairness-check';
import { countFieldTime } from '../../lib/field-time-live';
import type { FieldTimeRow } from './SeasonFieldTime';
import { shortName } from './DefenseGrid';
import { InningStrip } from './InningStrip';

// The Lineup Wall's right pane (wide screens only): what the plan on the left
// means, recomputed on every change. Who sits and when, what the fairness rules
// would flag if the plan holds, who the season says is owed innings and how
// much they play here, and who pitches when.
//
// Advisory only, bench-coach voice: it informs, it never changes the lineup.

const SectionTitle = ({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) => (
  <div className="flex items-baseline justify-between px-1 mb-2 mt-5 first:mt-0">
    <h3 className="text-sm font-semibold text-ink-2">{children}</h3>
    {right && <span className="text-[13px] text-ink-2 tabular-nums">{right}</span>}
  </div>
);

export const PlanConsequences = ({
  players, innings, numInnings, settings, seasonRows, showPitching, pitcherRows, onFieldTime,
}: {
  /** Present players, in batting order. */
  players: any[];
  innings: any[];
  numInnings: number;
  settings: any;
  /** Season field time, sorted most-owed first (buildFieldTimeRows). */
  seasonRows: FieldTimeRow[];
  /** Players-pitch only. */
  showPitching: boolean;
  pitcherRows: PitcherReadiness[];
  onFieldTime: () => void;
}) => {
  const shown = innings.slice(0, numInnings);
  const ids = players.map((p: any) => p.id);
  const counts = useMemo(() => countFieldTime(shown, ids), [innings, numInnings, players]); // eslint-disable-line react-hooks/exhaustive-deps
  const positionsOf = (id: string) =>
    Array.from({ length: numInnings }, (_, i) => shown[i]?.find((e: any) => e.id === id)?.position ?? 'BENCH');

  // Who sits most first; ties keep batting order.
  const benchRows = [...players].sort((a, b) => (counts.get(b.id)?.bench ?? 0) - (counts.get(a.id)?.bench ?? 0));
  const benchValues = ids.map(id => counts.get(id)?.bench ?? 0);
  const anyEH = ids.some(id => (counts.get(id)?.eh ?? 0) > 0);
  const maxBench = Math.max(0, ...benchValues);
  const minBench = Math.min(...benchValues, maxBench);
  const benchLine = maxBench === 0
    ? 'Nobody sits'
    : maxBench === minBench
      ? `Everyone sits ${maxBench}`
      : `${minBench} to ${maxBench} innings each`;

  // Read the plan as if it were played: every planned inning as a log.
  const findings = useMemo(() => {
    if (!anyFairnessRuleOn(settings) || numInnings === 0) return null;
    const logs: Record<number, { id: string; position: string }[]> = {};
    shown.forEach((inn: any[], i: number) => { logs[i + 1] = (inn || []).map((e: any) => ({ id: e.id, position: e.position })); });
    const names: Record<string, string> = {};
    players.forEach((p: any) => { names[p.id] = p.name; });
    return analyzeFairness(logs[numInnings] || [], logs, settings, names, numInnings);
  }, [innings, numInnings, players, settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const owed = seasonRows.filter(r => r.due && ids.includes(r.player.id));

  // Who is on the mound when, as runs of innings, with each arm's readiness.
  const runs = useMemo(() => {
    if (!showPitching) return [];
    const out: { id: string | null; from: number; through: number }[] = [];
    for (let i = 0; i < numInnings; i++) {
      const id = shown[i]?.find((e: any) => e.position === 'P')?.id ?? null;
      const last = out[out.length - 1];
      if (last && last.id === id) last.through = i + 1;
      else out.push({ id, from: i + 1, through: i + 1 });
    }
    return out;
  }, [showPitching, innings, numInnings]); // eslint-disable-line react-hooks/exhaustive-deps
  const readiness = new Map(pitcherRows.map(r => [r.player.id, r]));
  const nameOf = (id: string) => shortName(players.find((p: any) => p.id === id)?.name || '');
  const span = (from: number, through: number) => (from === through ? `Inning ${from}` : `Innings ${from} to ${through}`);

  return (
    <>
      <div className="flex items-center justify-center px-4 pt-4 pb-1 bg-surface">
        <span className="text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-2">This plan</span>
      </div>
      <div className="px-5 pt-2 pb-4 bg-surface">
        <h2 className="text-[27px] font-bold tracking-[-0.028em] leading-[1.15] text-ink">At a glance</h2>
        <p className="text-[14.5px] text-ink-2 mt-1.5 leading-snug">Change the grid and this side follows.</p>
      </div>

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4 pb-6">
        <SectionTitle right={benchLine}>Bench this game</SectionTitle>
        <div className="bg-surface rounded-card px-3 py-2">
          <div className="flex items-center gap-3 px-1 pb-2 text-[12px] text-ink-2">
            <span className="flex items-center gap-1.5"><span className="w-[12px] h-[12px] rounded-[4px] bg-accent" />Field</span>
            <span className="flex items-center gap-1.5"><span className="w-[12px] h-[12px] rounded-[4px] border-[1.5px] border-ink-mute" />Bench</span>
            {anyEH && (
              <span className="flex items-center gap-1.5">
                <span className="w-[12px] h-[12px] rounded-[4px] border-[1.5px] border-ink-mute flex items-center justify-center">
                  <span className="w-[4px] h-[4px] rounded-full bg-ink-2" />
                </span>
                Extra hitter
              </span>
            )}
          </div>
          {benchRows.map((p: any, idx: number) => {
            const c = counts.get(p.id);
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 px-1 py-1.5 ${idx === benchRows.length - 1 ? '' : 'border-b border-hairline'}`}
              >
                <span className="w-[96px] shrink-0 truncate text-[13.5px] font-semibold text-ink">{shortName(p.name)}</span>
                <span className="flex-1 min-w-0 overflow-hidden"><InningStrip positions={positionsOf(p.id)} /></span>
                <span className="shrink-0 text-[12.5px] text-ink-2 tabular-nums">
                  {[c?.bench ? `sits ${c.bench}` : '', c?.eh ? `EH ${c.eh}` : ''].filter(Boolean).join(', ') || 'plays all'}
                </span>
              </div>
            );
          })}
        </div>

        {findings && (
          <>
            <SectionTitle>Fairness, if the plan holds</SectionTitle>
            <div className="bg-surface rounded-card px-4 py-3 space-y-1.5">
              {findings.length === 0 ? (
                <p className="text-[13.5px] text-ink-2">Nothing your fairness rules would flag.</p>
              ) : (
                <>
                  {findings.slice(0, FAIRNESS_VISIBLE_CAP).map((f, i) => (
                    <p key={`${f.rule}-${i}`} className="text-[13.5px] text-ink leading-snug">{f.message}</p>
                  ))}
                  {findings.length > FAIRNESS_VISIBLE_CAP && (
                    <p className="text-[12.5px] text-ink-2">+{findings.length - FAIRNESS_VISIBLE_CAP} more</p>
                  )}
                </>
              )}
            </div>
          </>
        )}

        <SectionTitle>Season field time</SectionTitle>
        <div className="bg-surface rounded-card">
          {owed.length === 0 ? (
            <p className="px-4 py-3 text-[13.5px] text-ink-2">Playing time is even so far this season.</p>
          ) : (
            owed.map((r, idx) => (
              <div
                key={r.player.id}
                className={`flex items-baseline gap-3 px-4 py-2.5 ${idx === owed.length - 1 ? '' : 'border-b border-hairline'}`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-[13.5px] font-semibold text-ink">{shortName(r.player.name)}</span>
                  <span className="block text-[12.5px] text-ink-2 tabular-nums">
                    On the bench {Math.round(r.share * 100)}% of innings this season
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-semibold text-ink tabular-nums">
                  Plays {counts.get(r.player.id)?.field ?? 0} of {numInnings} here
                </span>
              </div>
            ))
          )}
          <button onClick={onFieldTime} className="w-full flex items-center gap-2 px-4 py-3 border-t border-hairline text-left">
            <span className="flex-1 text-[13.5px] font-semibold text-ink">See season field time</span>
            <ChevronRight className="w-4 h-4 text-ink-mute shrink-0" />
          </button>
        </div>

        {showPitching && runs.length > 0 && (
          <>
            <SectionTitle>Pitching</SectionTitle>
            <div className="bg-surface rounded-card">
              {runs.map((run, idx) => {
                const r = run.id ? readiness.get(run.id) : undefined;
                return (
                  <div
                    key={`${run.from}-${run.id}`}
                    className={`flex items-baseline gap-3 px-4 py-2.5 ${idx === runs.length - 1 ? '' : 'border-b border-hairline'}`}
                  >
                    <span className="w-[96px] shrink-0 text-[12.5px] text-ink-2 tabular-nums">{span(run.from, run.through)}</span>
                    <span className="flex-1 min-w-0 truncate text-[13.5px] font-semibold text-ink">
                      {run.id ? nameOf(run.id) : 'Nobody at P'}
                    </span>
                    {r && (
                      <span className={`shrink-0 text-[12.5px] font-semibold tabular-nums ${r.status === 'rest' ? 'text-gold-ink' : 'text-ink-2'}`}>
                        {r.statusLabel}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};
